// In-CRM WebRTC softphone (desktop agents) built on JsSIP. The agent's SIP
// account registers to Asterisk over WSS; when the CRM click-to-call originates
// a call, Asterisk rings this registered endpoint, the phone auto-answers, and
// audio flows in the browser — no Linphone app needed.
//
// Remote agents (any WiFi, not the office LAN) connect through PUBLIC infra:
// the WSS URL is the public proxy in front of the PBX and media relays
// through coturn using the minted iceServers. iceTransportPolicy "all" keeps on-LAN agents direct.
//
// Usage:
//   import { initWebrtcPhone, stopWebrtcPhone, hangupWebrtc, isWebrtcRegistered } from "...";
//   initWebrtcPhone(config, { onRegistered, onIncoming, onConnected, onEnded, onError });
// `config` is the payload from telephony/webrtcConfig
//   { extension, password, wsServer, sipDomain, iceServers, iceTransportPolicy }.

import JsSIP from "jssip";

let ua = null;
let currentSession = null;
let remoteAudio = null;
let _iceServers = [];
let _iceTransportPolicy = "all";
// When the CRM originates a click-to-call, it calls expectOutboundLeg() so the
// resulting incoming INVITE (Asterisk ringing our extension) is auto-answered.
// Any OTHER incoming INVITE within no such window is a REAL inbound call (a
// customer calling back a SIM number) → we surface it for a screen-pop instead
// of auto-answering.
let _expectOutboundUntil = 0;

// Mark that we just triggered a click-to-call and expect our own leg to ring.
// Window default 45s (was 20s): a GSM dongle can take 10-15s to seize a SIM and
// ring, and the line may first be re-registered (ensureLineReady ~2-4s) — so a
// 20s window sometimes lapsed BEFORE our own INVITE arrived, and it was then
// mistaken for a customer callback → screen-pop on some other page and the call
// never reached the client. 45s comfortably covers the whole originate→ring path.
export function expectOutboundLeg(ms = 45000) {
  _expectOutboundUntil = Date.now() + ms;
}

// A mic stream captured up-front (on the page the agent clicked Call) and HELD
// so the auto-answer reuses it instead of doing its OWN getUserMedia — which
// would pop a SECOND permission prompt, possibly on a different page, and stall
// the call until accepted there. Consumed by the answer below, then released.
let _pendingMicStream = null;

// Ask for the microphone NOW (on the confirmation page, at click time) and KEEP
// the stream so the incoming-leg answer reuses it — one prompt, on the right
// page. Resolves true if the mic is available, false if blocked.
export async function warmUpMic() {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
    // Drop any earlier held stream first (avoid leaking a live mic track).
    if (_pendingMicStream) { try { _pendingMicStream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ } }
    _pendingMicStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    return true;
  } catch (e) {
    _pendingMicStream = null;
    return false; // NotAllowedError (blocked) / NotFoundError (no mic) / …
  }
}

// Release the microphone held by a session: stop() every local media track
// on its peer connection. If the mic track is left "live" after a call ends,
// the browser will NOT grant getUserMedia to the next call — it hangs with no
// permission prompt and the call never answers. Best-effort; safe on any state.
// (Ref: MediaStreamTrack.stop() is required to actually free the mic.)
function _releaseLocalMedia(session) {
  try {
    const pc = session && session.connection;
    if (!pc) return;
    // Senders cover tracks added to the PC; getLocalStreams is a fallback.
    const senders = (pc.getSenders && pc.getSenders()) || [];
    senders.forEach((s) => {
      if (s && s.track) {
        try { s.track.stop(); } catch (e) { /* ignore */ }
      }
    });
    if (pc.getLocalStreams) {
      pc.getLocalStreams().forEach((st) => {
        (st.getTracks ? st.getTracks() : []).forEach((t) => {
          try { t.stop(); } catch (e) { /* ignore */ }
        });
      });
    }
  } catch (e) {
    /* ignore */
  }
}

// Lazily create a single hidden <audio> element for the remote stream.
function ensureAudioEl() {
  if (remoteAudio) return remoteAudio;
  remoteAudio = document.createElement("audio");
  remoteAudio.autoplay = true;
  remoteAudio.setAttribute("playsinline", "");
  remoteAudio.style.display = "none";
  document.body.appendChild(remoteAudio);
  return remoteAudio;
}

// (Re)start the phone and register. Safe to call again — tears down first.
export function initWebrtcPhone(config, handlers = {}) {
  stopWebrtcPhone();
  ensureAudioEl();
  _iceServers = config.iceServers || [];
  // "all" (default) → try a direct/host path first, relay through TURN only when
  // there's no direct route (remote agent behind NAT). "relay" would force even
  // office agents through coturn — only for force-testing the relay path.
  _iceTransportPolicy = config.iceTransportPolicy || "all";

  const socket = new JsSIP.WebSocketInterface(config.wsServer);
  ua = new JsSIP.UA({
    sockets: [socket],
    uri: `sip:${config.extension}@${config.sipDomain}`,
    password: config.password,
    display_name: String(config.extension),
    register: true,
    // WebSocket keepalive (CRLF ping). Home / hotel / corporate routers drop
    // idle TCP connections; without this a remote agent silently loses
    // registration between calls (the socket dies but JsSIP doesn't notice until
    // the next action fails). The nginx proxy read timeout is 5400s, so the
    // server side won't be what drops us — this keeps the client end alive.
    keepAliveInterval: 30,
    // Registration lifetime. Keep it comfortably long (300s → JsSIP renews at
    // ~150s): a WebRTC client gets a NEW contact port on every re-REGISTER, and
    // with max_contacts=1 + remove_existing=yes the old contact is dropped — if
    // that renewal lands mid-call it briefly marks the endpoint Unreachable and
    // KILLS the ringing/active call. (120s was too aggressive and cut calls
    // short.) Reachability gaps are covered by qualify (OPTIONS) on the PBX +
    // fast WSS recovery below — not by hammering REGISTER.
    register_expires: 300,
    connection_recovery_min_interval: 2,
    connection_recovery_max_interval: 15,
    session_timers: false,
  });

  ua.on("registered", () => handlers.onRegistered && handlers.onRegistered());
  ua.on("unregistered", () => handlers.onUnregistered && handlers.onUnregistered());
  ua.on("registrationFailed", (e) => handlers.onError && handlers.onError("register", e));
  // The registration EXPIRED (its lifetime elapsed and the re-REGISTER did not
  // renew it — e.g. the WebRTC JWT token expired, so the PBX drops the contact).
  // JsSIP fires `unregistered` for this, but as a safety net also treat a fired
  // `registrationExpired` as "line down" so the badge can never sit on "ready"
  // with a dead line.
  if (ua.on) ua.on("registrationExpired", () => handlers.onUnregistered && handlers.onUnregistered());
  // WebSocket to the PBX (:8089) couldn't be reached / dropped. This — NOT
  // registrationFailed — is the event that fires when the socket never opens
  // (agent off the office LAN / VPN down, PBX offline, :8089 blocked): with no
  // socket, JsSIP never even sends a REGISTER, so registrationFailed stays
  // silent and the UI would sit on "Connecting…" forever. Surface it as a
  // "register" error so the failure counter trips the offline state. JsSIP
  // auto-reconnects (connection_recovery_*), and a later "registered" clears it.
  ua.on("disconnected", (e) => {
    // A socket close that carries an abnormal WS code (anything but a clean 1000)
    // means the line is really gone — report it EVEN IF JsSIP still thinks it's
    // registered (its internal flag lags a token-expiry/server-side drop, which
    // is exactly the "badge says ready but calls don't ring" bug). Only a clean
    // close while still registered is a harmless reconnect blip we can ignore.
    const code = e && e.socket && e.socket.weight != null ? null : (e && e.code);
    const cleanClose = code === 1000 || code === undefined;
    if (cleanClose && ua && ua.isRegistered && ua.isRegistered()) return;
    handlers.onError && handlers.onError("register", e);
  });

  ua.on("newRTCSession", (data) => {
    const session = data.session;
    if (session.direction !== "incoming") return;

    // Is this OUR click-to-call leg? DEFINITIVE signal first: the CRM tags the
    // INVITE that rings us with the custom header `X-CRM-Autoanswer: 1` (added by
    // the backend originate). If present, this is unquestionably our own leg — no
    // timing guess needed, even if a slow dongle made it arrive late. Fall back to
    // the time window only when the header is absent (older PBX / header stripped).
    let headerSaysMine = false;
    let _hdrRaw = null;
    let _hdrVia = "none";
    try {
      // JsSIP 3.13: the incoming INVITE is `data.request` (NOT session.request,
      // which is undefined for an incoming session). Read the custom header off it.
      if (data.request && typeof data.request.getHeader === "function") {
        _hdrRaw = data.request.getHeader("X-CRM-Autoanswer");
        if (_hdrRaw != null) _hdrVia = "data.request.getHeader";
      }
      if (_hdrRaw == null && session && typeof session.getHeader === "function") {
        _hdrRaw = session.getHeader("X-CRM-Autoanswer");
        if (_hdrRaw != null) _hdrVia = "session.getHeader";
      }
      headerSaysMine = !!_hdrRaw && String(_hdrRaw).trim() === "1";
    } catch (e) { /* ignore */ }
    const isExpectedOutbound = headerSaysMine || Date.now() < _expectOutboundUntil;
    // Keep one compact diagnostic line — invaluable if a click-to-call ever pops
    // as "Customer" again (tells auto-answer vs screen-pop and the header value).
    console.log(`[webrtc] incoming → ${isExpectedOutbound ? "AUTO-ANSWER" : "SCREEN-POP"} (X-CRM-Autoanswer=${_hdrRaw == null ? "none" : _hdrRaw}, via ${_hdrVia})`);

    // A REAL second inbound while already busy → reject with 486 Busy Here so
    // Asterisk routes it onward (fallback agents). One extension = one call.
    // BUT an expected click-to-call leg must ALWAYS be accepted — the agent
    // deliberately started a new call, so any lingering session is stale and
    // gets torn down below (this is what caused the widget to flash + drop
    // after several call cycles when an old session hadn't been cleaned up).
    if (!isExpectedOutbound && currentSession && !currentSession.isEnded()) {
      try { session.terminate({ status_code: 486, reason_phrase: "Busy Here" }); } catch (e) { /* ignore */ }
      return;
    }
    // Click-to-call (or no active call): take over. Kill any stale session so
    // the new leg is never rejected as "busy" by a ghost of a previous call.
    if (currentSession && currentSession !== session) {
      try { if (!currentSession.isEnded()) currentSession.terminate(); } catch (e) { /* ignore */ }
    }
    currentSession = session;

    // Pipe the remote audio to the hidden <audio> element, and watch the ICE
    // connection so a call that "connects" but has no media (the classic WebRTC
    // one-way / no-audio caused by NAT / firewall blocking RTP, or no TURN) is
    // surfaced to the agent instead of looking like a silent call.
    session.on("peerconnection", (e) => {
      const pc = e.peerconnection;
      pc.addEventListener("track", (ev) => {
        if (ev.streams && ev.streams[0]) ensureAudioEl().srcObject = ev.streams[0];
      });

      // ── ICE gathering watchdog (fixes the empty-SDP 200 OK) ────────────
      // Asterisk 16 has NO trickle ICE, so JsSIP must send the 200 OK with the
      // FULL candidate list — it waits for iceGatheringState === "complete"
      // before answering. On these agent PCs gathering NEVER completes: the
      // Tailscale IPv6 candidate (fd7a:…) has no reachable STUN/TURN over v6,
      // so its srflx/relay probes hang for the full ICE timeout and the
      // "complete" event (and the null end-of-candidates) never fires. JsSIP
      // then sends a 200 OK with Content-Length: 0 (no SDP) — which is exactly
      // what the PBX saw across 34 responses — so Asterisk never learns our
      // ice-ufrag/pwd and every pair fails 0 bytes.
      //
      // Fix: once we've gathered the candidates that matter (host + srflx +
      // relay all arrive within ~1s), tell JsSIP to stop waiting for the doomed
      // IPv6 probes, so the 200 OK goes out WITH the answer. Belt-and-suspenders:
      // also fire after a hard 2.5s cap even if the relay is slow.
      //
      // Use JsSIP's OWN supported hook for this: RTCSession emits a session-level
      // "icecandidate" event carrying { candidate, ready }, and calling ready()
      // sets its internal _iceReady and resolves _createLocalDescription
      // (jssip/lib/RTCSession.js — ready() ~L1442, emit ~L1464, check ~L1425).
      //
      // Do NOT instead re-apply pc.setLocalDescription(pc.localDescription).
      // By the time this fires, JsSIP has already run createAnswer() and set the
      // local answer, so signalingState is "stable" — re-applying a local answer
      // there is invalid, and Firefox rejects it with "Cannot set local answer
      // when createAnswer has not been called." Chrome tolerates it, which is why
      // that approach looked like it worked. Worse, setLocalDescription returns a
      // PROMISE, so a synchronous try/catch never caught the rejection: gathering
      // was then never ended and the empty-SDP 200 OK came straight back on
      // Firefox. This path touches no PeerConnection state at all.
      let iceReadyFired = false;
      let sawRelay = false;
      let capTimer = null;
      const finishGathering = (ready, why) => {
        if (iceReadyFired) return;
        iceReadyFired = true;
        clearTimeout(capTimer);
        console.log(`[ICE] ending gathering early (${why}); state was ${pc.iceGatheringState}`);
        try {
          ready();
        } catch (err) {
          console.warn("[ICE] ready() failed:", err);
        }
      };
      session.on("icecandidate", (ev) => {
        const ready = ev && ev.ready;
        if (typeof ready !== "function") return;
        // Arm the cap on the first candidate — that is the earliest point a
        // ready() callback exists to call.
        if (!capTimer) capTimer = setTimeout(() => finishGathering(ready, "2.5s cap"), 2500);
        if (ev.candidate && ev.candidate.type === "relay" && !sawRelay) {
          sawRelay = true;
          // We have a usable relay; let a beat pass for srflx, then finish.
          setTimeout(() => finishGathering(ready, "relay ready"), 400);
        }
      });

      // ── ICE diagnostics ───────────────────────────────────────────────
      // Log every LOCAL candidate we gather (host / srflx / relay) so an
      // "ICE failed" is debuggable from the console alone: if no `relay`
      // line appears, the browser never reached coturn (creds/URL/UDP
      // blocked); if relay appears on BOTH ends but ICE still fails, the
      // relay↔relay path is being refused on the TURN server. Prefixed
      // [ICE] for easy filtering.
      pc.addEventListener("icecandidate", (ev) => {
        if (ev.candidate && ev.candidate.candidate) {
          const c = ev.candidate;
          console.log(`[ICE] local candidate: ${c.type || "?"}  ${c.address || ""}:${c.port || ""}  (${c.protocol || ""})`);
        } else {
          console.log("[ICE] local candidate gathering DONE");
        }
      });
      // Dump the SELECTED / attempted candidate pairs when ICE fails, so we
      // see exactly which pair was tried and its state (the thing about:webrtc
      // shows, but straight in the console).
      pc.addEventListener("iceconnectionstatechange", async () => {
        const st = pc.iceConnectionState;
        console.log(`[ICE] connectionState → ${st}`);
        if (st === "failed" || st === "disconnected") {
          try {
            const stats = await pc.getStats();
            stats.forEach((r) => {
              if (r.type === "candidate-pair") {
                console.log(
                  `[ICE] pair state=${r.state} nominated=${r.nominated} ` +
                  `bytesSent=${r.bytesSent || 0} bytesRecv=${r.bytesReceived || 0}`
                );
              }
              if (r.type === "local-candidate" || r.type === "remote-candidate") {
                console.log(`[ICE] ${r.type}: ${r.candidateType} ${r.address || r.ip || ""}:${r.port || ""}`);
              }
            });
          } catch (err) { /* ignore */ }
        }
      });

      let iceGraceTimer = null;
      pc.addEventListener("iceconnectionstatechange", () => {
        if (currentSession !== session) return;
        const st = pc.iceConnectionState;
        if (st === "failed") {
          // Definitive media failure → the call has no audio path.
          if (iceGraceTimer) { clearTimeout(iceGraceTimer); iceGraceTimer = null; }
          handlers.onError && handlers.onError("media", { state: st });
        } else if (st === "disconnected") {
          // May be a transient blip; only flag if it doesn't recover in ~6s.
          if (iceGraceTimer) clearTimeout(iceGraceTimer);
          iceGraceTimer = setTimeout(() => {
            if (currentSession === session && pc.iceConnectionState === "disconnected") {
              handlers.onError && handlers.onError("media", { state: "disconnected" });
            }
          }, 6000);
        } else if (st === "connected" || st === "completed") {
          if (iceGraceTimer) { clearTimeout(iceGraceTimer); iceGraceTimer = null; }
        }
      });
    });
    // Only the CURRENT session drives the UI. A stale session's end/fail (e.g.
    // one that already ended just as a new call started) must NOT tear down the
    // widget for the call that replaced it.
    session.on("confirmed", () => {
      if (currentSession === session) handlers.onConnected && handlers.onConnected();
    });
    session.on("ended", () => {
      // ALWAYS release the mic for THIS session, even if it's stale — otherwise
      // the microphone track stays "live" and the browser won't grant
      // getUserMedia to the NEXT call (it hangs with no permission prompt and
      // the call never answers). See _releaseLocalMedia.
      _releaseLocalMedia(session);
      if (currentSession !== session) return;
      currentSession = null;
      handlers.onEnded && handlers.onEnded();
    });
    session.on("failed", (e) => {
      _releaseLocalMedia(session);
      if (currentSession !== session) return;
      currentSession = null;
      handlers.onError && handlers.onError("call", e);
      handlers.onEnded && handlers.onEnded();
    });

    // ── Diagnostics ──────────────────────────────────────────────────
    // When an auto-answered click-to-call leg never picks up (Asterisk then
    // reports no-answer / Q.850 cause 19) with NO visible console error, the
    // usual reason is that getUserMedia() (needed to answer with a mic) failed
    // ASYNCHRONOUSLY — JsSIP swallows it into this event instead of throwing.
    // Log the real DOMException so the cause is obvious:
    //   NotAllowedError  → mic permission denied/blocked
    //   NotReadableError → mic busy / grabbed by another app or tab
    //   NotFoundError    → no microphone device
    session.on("getusermediafailed", (err) => {
      const name = (err && (err.name || (err.constructor && err.constructor.name))) || "unknown";
      console.error(
        "[webrtc] MIC (getUserMedia) FAILED — this is why the call doesn't connect (no-answer). Reason:",
        name,
        err
      );
      handlers.onError && handlers.onError("getusermedia", err);
    });
    session.on("peerconnection:createanswerfailed", (e) =>
      console.error("[webrtc] createAnswer failed:", e)
    );
    session.on("peerconnection:setlocaldescriptionfailed", (e) =>
      console.error("[webrtc] setLocalDescription failed:", e)
    );
    session.on("peerconnection:setremotedescriptionfailed", (e) =>
      console.error("[webrtc] setRemoteDescription failed:", e)
    );

    _expectOutboundUntil = 0; // consume the outbound window

    if (isExpectedOutbound) {
      // Click-to-call leg — auto-answer. On-LAN agents reach the PBX directly
      // (host candidate); remote agents relay their media through coturn using
      // the minted iceServers. iceTransportPolicy "all" lets each pick the best
      // path (direct when possible, relay when NAT'd).
      handlers.onIncoming && handlers.onIncoming(session);
      try {
        // Reuse the mic stream captured by warmUpMic() at click time, so JsSIP
        // does NOT run its own getUserMedia (which would prompt again, maybe on
        // another page, and block the call). If none was captured (permission
        // already remembered, or warmUp skipped), fall back to constraints.
        const answerOpts = {
          pcConfig: { iceServers: _iceServers, iceTransportPolicy: _iceTransportPolicy },
        };
        if (_pendingMicStream && _pendingMicStream.getAudioTracks && _pendingMicStream.getAudioTracks().length) {
          answerOpts.mediaStream = _pendingMicStream;
          _pendingMicStream = null; // the session now owns/releases it
        } else {
          answerOpts.mediaConstraints = { audio: true, video: false };
        }
        session.answer(answerOpts);
      } catch (err) {
        handlers.onError && handlers.onError("answer", err);
      }
    } else {
      // REAL inbound call — a customer calling back a SIM number. Do NOT
      // auto-answer; surface the caller for a screen-pop and let the agent
      // accept (answerWebrtc) or reject (hangupWebrtc).
      const caller =
        (session.remote_identity &&
          session.remote_identity.uri &&
          session.remote_identity.uri.user) ||
        "";
      handlers.onInbound && handlers.onInbound(caller, session);
    }
  });

  ua.start();
  return ua;
}

// Answer the current (inbound) call — used when the agent accepts a customer
// callback after the screen-pop.
// Release a mic stream captured by warmUpMic() that was never consumed by an
// answer (e.g. the call failed with noGateway, or the agent cancelled). Prevents
// a live mic track lingering (which also blocks the browser's next getUserMedia).
export function releasePendingMic() {
  if (_pendingMicStream) {
    try { _pendingMicStream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    _pendingMicStream = null;
  }
}

export function answerWebrtc() {
  if (!currentSession) return;
  try {
    currentSession.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: _iceServers, iceTransportPolicy: _iceTransportPolicy },
    });
  } catch (e) {
    /* ignore */
  }
}

// Hang up the current call (if any).
export function hangupWebrtc() {
  if (currentSession) {
    const s = currentSession;
    try { s.terminate(); } catch (e) { /* ignore */ }
    // Release the mic immediately (don't wait for the async "ended" event) so a
    // fast next call always gets a fresh getUserMedia.
    _releaseLocalMedia(s);
    currentSession = null;
  }
  // Detach the remote audio so a stale stream doesn't linger.
  if (remoteAudio) { try { remoteAudio.srcObject = null; } catch (e) { /* ignore */ } }
}

// Mute/unmute the local mic. Returns the new muted state.
export function setWebrtcMuted(muted) {
  if (!currentSession) return false;
  try {
    if (muted) currentSession.mute({ audio: true });
    else currentSession.unmute({ audio: true });
  } catch (e) { /* ignore */ }
  return !!muted;
}

// Stop and unregister the phone entirely.
export function stopWebrtcPhone() {
  hangupWebrtc();
  if (ua) {
    try { ua.stop(); } catch (e) { /* ignore */ }
    ua = null;
  }
}

export function isWebrtcRegistered() {
  return !!(ua && ua.isRegistered && ua.isRegistered());
}

export function isWebrtcInCall() {
  return !!currentSession;
}

// Force a fresh REGISTER on the existing UA (renew the registration before its
// lifetime elapses / after a silent expiry). Cheap — reuses the open WSS socket
// and the same creds; the PBX refreshes the contact and its expiry timer. Safe
// to call anytime EXCEPT it's pointless with no UA. Returns true if a register
// was issued. NOTE: if the SIP SECRET itself was rotated (extension recreated),
// a re-register with the OLD creds still fails — that needs a full re-init with
// a freshly fetched password (the caller re-runs initWebrtcPhone for that).
export function reRegisterWebrtc() {
  if (!ua || !ua.register) return false;
  try { ua.register(); return true; } catch (e) { return false; }
}
